import {
  accessCardsDetailsOptions,
  createMunuUser,
  updateMunuUser,
  useMunuDealIds,
  useMunuVariCompany,
  useUser,
  useCompany,
  munuVariCompanyOptions,
  munuCompanyUsersOptions,
  companyOptions,
  deactivateMunuUser,
  reactivateMunuUser,
  snackSupplierOptions,
  foodUserOptions,
  foodCompanyOptions,
  userMetadataOptions,
  useSubdivisionById,
} from "@/api";
import {
  Company,
  CompanyType,
  FoodTransaction,
  MunuUserData,
  NotificationType,
  User,
  UserAuthMetadata,
} from "@/types";
import {
  Button,
  Dropdown,
  Icon,
  LoadingSpinner,
  PopupCard,
} from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useMemo, useState } from "react";
import styles from "@/styles/CompanySettings.module.scss";
import foodStyles from "@/styles/Food.module.scss";
import { Trans, useTranslation } from "react-i18next";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import {
  useLoggedInUser,
  useRouterStore,
  useSnackbarStore,
} from "@/store/zustandStore";
import {
  calculateLunchPrice,
  firebaseTimestampToDate,
  getBuildingId,
  notifyApiError,
  NOK,
  NUMERIC_DATE_TIME_LOCALE,
  timestampSortingFn,
} from "@/lib";
import { hasPendingRefund } from "@/lib/food";
import { useMunuUserDetails } from "@/hooks/useMunuUserDetails";
import { AnimatedTickingNumber } from "@/components/AnimatedTickingNumber";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "@/components/table/Table";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import FoodDealModal from "@/components/food/FoodDealModal";
import DeactivateFoodUserButton from "@/components/food/DeactivateFoodUserButton";
import RefundFoodBalanceButton from "@/components/food/RefundFoodBalanceButton";
import ActivateFoodUserForm from "@/components/food/ActivateFoodUserForm";
import DisabledUserFoodPlaceholder from "@/components/food/DisabledUserFoodPlaceholder";
import { useFoodUserData } from "@/hooks/useFoodUserData";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute(
  "/_portal/company/$companyId/users/$userId/food",
)({
  component: ComponentWrapper,
  loader: ({ params }) => {
    const { queryClient, hasWelcomeFood, supplierCompanyId, user } =
      useRouterStore.getState();
    const { companyId } = params;
    if (!queryClient) return;
    if (hasWelcomeFood) {
      // Preload Welcome Food stuff
      queryClient.ensureQueryData(snackSupplierOptions(supplierCompanyId));
      queryClient.ensureQueryData(
        foodUserOptions(params.userId, companyId, supplierCompanyId),
      );
      queryClient.ensureQueryData(
        foodUserOptions(
          params.userId,
          companyId,
          supplierCompanyId,
          user.language,
        ),
      );
      queryClient.ensureQueryData(foodCompanyOptions(companyId));
      queryClient.ensureQueryData(userMetadataOptions(params.userId));
    } else {
      // Preload Munu stuff
      queryClient.ensureQueryData(munuVariCompanyOptions(companyId));
      queryClient.ensureQueryData(munuCompanyUsersOptions(companyId));
      queryClient.ensureQueryData(
        accessCardsDetailsOptions(params.userId, true),
      );
    }
    // Preload stuff needed for either food system
    queryClient.ensureQueryData(companyOptions(companyId));
  },
});

function ComponentWrapper() {
  return (
    <Suspense fallback={<Loader />}>
      <UserFoodSettings />
    </Suspense>
  );
}

function UserFoodSettings() {
  const { userId } = Route.useParams();
  const { hasMunu, hasWelcomeFood } = useWelcomeFoodAccess(false, false, true);
  const { data: user } = useUser(userId);
  const { data: company } = useCompany(user.buildingTenantId);
  const navigate = useNavigate();

  // Add food tab route guard
  const isExternalCompany = company?.companyType === CompanyType.EXTERNAL;
  if (isExternalCompany) {
    navigate({ to: "/401", replace: true });
    return null;
  }

  return hasMunu ? (
    <MunuSettings company={company} />
  ) : (
    hasWelcomeFood && <WelcomeFoodSettings user={user} company={company} />
  );
}

function Loader() {
  return (
    <div
      style={{
        margin: "var(--wlcm-spacing-xl) auto",
        width: "max-content",
      }}
    >
      <LoadingSpinner />
    </div>
  );
}

const columnHelper = createColumnHelper<FoodTransaction>();

function WelcomeFoodSettings({
  user,
  company,
}: {
  user: User;
  company: Company;
}) {
  const { t, i18n } = useTranslation();
  const currentUser = useLoggedInUser();
  const { allowFoodAdminAccess } = useWelcomeFoodAccess(true);
  const { foodUser, isLoadingFoodUser, usersDeals } = useFoodUserData(user);
  const { data: userMetadata } = useBuildingQuery({
    ...userMetadataOptions(user.userId),
  }) as { data?: UserAuthMetadata };
  const [dealModalOpen, setDealModalOpen] = useState(false);

  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        cell: (info) =>
          NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            firebaseTimestampToDate(info.getValue()),
          ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("PAGES.FOOD_SETTINGS.TIMESTAMP")}
          />
        ),
        sortingFn: timestampSortingFn,
        meta: {
          label: t("PAGES.FOOD_SETTINGS.TIMESTAMP"),
        },
      }),
      columnHelper.accessor("type", {
        id: "description",
        cell: (info) => {
          const type = info.getValue();
          if (type && type.startsWith("eat-")) {
            return t("PAGES.LUNCH.TRANSACTION_TYPES.LUNCH_EATEN");
          }
          return type;
        },
        header: t("PAGES.FOOD_SETTINGS.DESCRIPTION"),
        meta: {
          label: t("PAGES.FOOD_SETTINGS.DESCRIPTION"),
        },
      }),
      columnHelper.accessor("amount", {
        cell: (info) => NOK.format(info.getValue()),
        header: t("PAGES.FOOD_SETTINGS.AMOUNT"),
        meta: {
          label: t("PAGES.FOOD_SETTINGS.AMOUNT"),
        },
      }),
    ],
    [],
  );

  const eatingEvents = useMemo(() => {
    return allowFoodAdminAccess || user.userId === currentUser.userId // since Food Admins (and users themselves) get all the data, filter out topups so table makes sense
      ? foodUser?.history?.filter((event) => event.eventId) || []
      : foodUser?.history || [];
  }, [foodUser, allowFoodAdminAccess, user, currentUser]);

  return user.integratedWithWelcomeFood ? (
    isLoadingFoodUser ? (
      <div
        style={{
          margin: "var(--wlcm-spacing-xl) 0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner />
      </div>
    ) : (
      <>
        <div className={styles.divWithIcon}>
          <Icon name="check_circle" className="greenIcon" />
          <p>
            {t("PAGES.FOOD_SETTINGS.USER_HAS_DEAL")}{" "}
            <strong>{usersDeals?.[0]?.name}</strong>
            {usersDeals?.length === 2 && (
              <>
                , <strong>{usersDeals?.[1]?.name}</strong>
              </>
            )}
          </p>
          {!userMetadata?.disabled && (
            <button
              style={{
                padding: 0,
                background: "none",
                border: "none",
                outline: "none",
                color: "var(--wlcm-color-blue)",
                marginLeft: "var(--wlcm-spacing-sm)",
                cursor: "pointer",
                textWrap: "nowrap",
              }}
              onClick={() => setDealModalOpen(true)}
            >
              <Icon name="edit" style={{ verticalAlign: "middle" }} />{" "}
              {t("PAGES.FOOD_SETTINGS.CHANGE")}
            </button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: "var(--wlcm-spacing-sm)",
            margin: "var(--wlcm-spacing-md) 0",
            flexWrap: "wrap",
          }}
        >
          <h3 className={styles.numberCard}>
            {t("PAGES.FOOD_SETTINGS.LUNCHES_EATEN")}
            <span className="sr-only">: </span>
            <strong>
              <AnimatedTickingNumber to={eatingEvents.length} />
            </strong>
          </h3>
          {usersDeals && (
            <div
              className={`${foodStyles.lunchDealCard} ${styles.numberCard} ${styles.purple}`}
            >
              <p>
                {t("PAGES.FOOD_SETTINGS.PRICE_PER_LUNCH")}
                <span className="sr-only">: </span>
              </p>
              <div
                className={foodStyles.multiLunchDealCardInner}
                style={{
                  gridTemplateColumns:
                    usersDeals.length > 1 ? "1fr auto 1fr" : "1fr",
                }}
              >
                <p>
                  <strong>
                    <AnimatedTickingNumber
                      to={calculateLunchPrice(
                        usersDeals[0]?.price ?? 0,
                        usersDeals[0]?.subsidyPercentage,
                      )}
                      formatter={NOK.format}
                    />
                  </strong>
                  {
                    /* Only display variant name if user has more than 1 deal */
                    usersDeals.length > 1 && (
                      <>
                        <br />
                        <span className={foodStyles.lunchVariantName}>
                          {usersDeals[0].lunchVariant?.name}
                        </span>
                      </>
                    )
                  }
                </p>
                {usersDeals.length > 1 && (
                  <>
                    <p>
                      <strong style={{ opacity: 0.25 }}>/</strong>
                    </p>
                    <p>
                      <strong>
                        <AnimatedTickingNumber
                          to={calculateLunchPrice(
                            usersDeals[1]?.price ?? 0,
                            usersDeals[1]?.subsidyPercentage,
                          )}
                          formatter={NOK.format}
                        />
                      </strong>
                      <br />
                      <span className={foodStyles.lunchVariantName}>
                        {usersDeals[1]?.lunchVariant?.name}
                      </span>
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
          <div className={styles.spacer}>{/* SPACER DIV */}</div>
        </div>
        <h3>{t("PAGES.FOOD_SETTINGS.LUNCH_HISTORY")}</h3>
        <Table
          name="user-eating-history"
          columns={columns}
          data={eatingEvents}
          defaultSorting={[
            {
              id: "timestamp",
              desc: true,
            },
          ]}
          noDataMessage={t("PAGES.FOOD_SETTINGS.NO_TRANSACTIONS")}
        />
        <DeactivateFoodUserButton
          userId={user.userId}
          buildingTenantId={user.buildingTenantId}
        />
        <PopupCard
          onClose={() => setDealModalOpen(false)}
          open={dealModalOpen && !!foodUser && !userMetadata?.disabled}
        >
          <FoodDealModal
            users={[user]}
            company={company}
            onClose={() => setDealModalOpen(false)}
            currentDealIds={foodUser?.dealIds}
          />
        </PopupCard>
      </>
    )
  ) : userMetadata?.disabled ? (
    <DisabledUserFoodPlaceholder icon="no_burger" />
  ) : (
    <>
      <ActivateFoodUserForm
        user={user}
        company={company}
        hasPendingRefund={hasPendingRefund(foodUser?.pendingTransactions)}
      />
      <RefundFoodBalanceButton
        userId={user.userId}
        buildingTenantId={user.buildingTenantId}
        currentBalance={foodUser?.currentBalance ?? 0}
        pendingTransactions={foodUser?.pendingTransactions}
        deactivated={user.integratedWithWelcomeFood === false}
      />
    </>
  );
}

function MunuSettings({ company }: { company: Company }) {
  const { userId } = Route.useParams();
  const { data: user } = useUser(userId);
  const { t } = useTranslation();

  if (!company) return <Loader />;

  if (!company.hasSubdivisionLink) {
    return <MunuSettingsParent user={user} />;
  }
  if (!user.subdivisionId) {
    return (
      <div className={styles.foodPlaceholder}>
        <Icon name="no_burger" />
        <p>{t("PAGES.FOOD_SETTINGS.USER_MUST_HAVE_SUBDIVISION")}</p>
      </div>
    );
  }
  return <MunuSettingsSubdivision user={user} company={company} />;
}

function MunuSettingsParent({ user }: { user: User }) {
  const { userId } = Route.useParams();
  const { data: munuCompany } = useMunuVariCompany(user.buildingTenantId);
  const {
    isLoading: munuUserLoading,
    munuUser,
    physicalAccessCardNumber,
  } = useMunuUserDetails(userId);
  const { t } = useTranslation();

  if (!munuCompany?.data?.available_deals)
    return (
      <div className={styles.foodPlaceholder}>
        <Icon name="no_burger" />
        <p>{t("PAGES.FOOD_SETTINGS.PLEASE_LINK_FIRST")}</p>
      </div>
    );

  return (
    <>
      {munuUserLoading ? (
        <Loader />
      ) : munuUser ? (
        <MunuUserOverview
          user={user}
          munuUser={munuUser}
          accessCardNumber={physicalAccessCardNumber || ""}
        />
      ) : !physicalAccessCardNumber ? (
        <div className={styles.foodPlaceholder}>
          <Icon name="badge" className="dangerIcon" />
          <p>{t("PAGES.FOOD_SETTINGS.NO_ACCESS_CARD")}</p>
        </div>
      ) : (
        <MunuCreateUser
          user={user}
          availableDeals={munuCompany.data.available_deals}
          accessCardNumber={physicalAccessCardNumber}
        />
      )}
    </>
  );
}

function MunuSettingsSubdivision({
  user,
  company,
}: {
  user: User;
  company: Company;
}) {
  const { userId } = Route.useParams();
  const { data: subdivisionMunu, isLoading: subdivisionMunuLoading } =
    useBuildingQuery(
      munuVariCompanyOptions(company.buildingTenantId, user.subdivisionId),
    );
  const { data: subdivision } = useSubdivisionById(
    user.subdivisionId,
    company.buildingTenantId,
  );
  const {
    isLoading: munuUserLoading,
    munuUser,
    physicalAccessCardNumber,
  } = useMunuUserDetails(userId);
  const { t } = useTranslation();

  if (subdivisionMunuLoading) return <Loader />;

  const subdivisionLinked =
    subdivisionMunu?.status === "200" &&
    subdivisionMunu?.data?.available_deals?.length;

  if (!subdivisionLinked) {
    return (
      <div className={styles.foodPlaceholder}>
        <Icon name="no_burger" />
        <p>
          {t("PAGES.FOOD_SETTINGS.SUBDIVISION_NOT_LINKED", {
            subdivisionName: subdivision?.name ?? user.subdivisionId,
          })}
        </p>
      </div>
    );
  }

  const availableDeals = subdivisionMunu.data.available_deals;

  return (
    <>
      {munuUserLoading ? (
        <Loader />
      ) : munuUser ? (
        <MunuUserOverview
          user={user}
          munuUser={munuUser}
          accessCardNumber={physicalAccessCardNumber || ""}
          subdivisionId={user.subdivisionId}
        />
      ) : !physicalAccessCardNumber ? (
        <div className={styles.foodPlaceholder}>
          <Icon name="badge" className="dangerIcon" />
          <p>{t("PAGES.FOOD_SETTINGS.NO_ACCESS_CARD")}</p>
        </div>
      ) : (
        <MunuCreateUser
          user={user}
          availableDeals={availableDeals}
          accessCardNumber={physicalAccessCardNumber}
          subdivisionId={user.subdivisionId}
        />
      )}
    </>
  );
}

const MunuCreateUser = ({
  user,
  availableDeals,
  accessCardNumber,
  subdivisionId,
}: {
  user: User;
  availableDeals: string[];
  accessCardNumber: string;
  subdivisionId?: string;
}) => {
  const { addNotification } = useSnackbarStore();
  const [selectedDeal, setSelectedDeal] = useState<
    { label: string; value: string } | undefined
  >(undefined);
  const { data: deals } = useMunuDealIds();
  const [isPushingMunuUser, setIsPushingMunuUser] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const dealDropdownOptions = useMemo(() => {
    return deals
      .filter((deal) => availableDeals.find((id) => deal.id === id))
      .map((deal) => ({ value: deal.id, label: deal.name }));
  }, [deals]);

  const pushUserToMunu = async () => {
    if (!selectedDeal) return;
    setIsPushingMunuUser(true);
    try {
      await createMunuUser({
        phone: user.phone,
        email: user.email,
        userId: user.userId,
        lastName: user.lastName,
        firstName: user.firstName,
        accessCard: accessCardNumber,
        dealId: +selectedDeal.value,
        buildingTenantId: user.buildingTenantId,
        ...(subdivisionId && { subdivisionId }),
      });
      addNotification(
        t("NOTIFICATIONS.DOCUMENT_UPDATED", {
          entity: `${user.firstName} ${user.lastName}`,
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "munu-company-users"],
      });
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: { entity: `${user.firstName} ${user.lastName}` },
        fallbackKey: "UPDATE_FAILED",
      });
    }
    setIsPushingMunuUser(false);
  };

  return (
    <>
      {user.phone && user.phone.length > 0 ? (
        <>
          <p
            className="banner"
            style={{ marginBottom: "var(--wlcm-spacing-md)" }}
          >
            <Icon name="info" />
            {t("PAGES.FOOD_SETTINGS.NOT_CONNECTED")}
          </p>
          <h2 className="h3">{t("PAGES.FOOD_SETTINGS.CONNECT_USER")}</h2>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--wlcm-spacing-sm)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--wlcm-spacing-xs)",
              }}
            >
              <Icon name="info" className="blueIcon" />
              <p>
                {t("PAGES.FOOD_SETTINGS.WILL_BE_CONNECTED_WITH")}{" "}
                {t("PAGES.FOOD_SETTINGS.ACCESS_CARD_NUMBER").toLowerCase()}:{" "}
                <span className="monospace">{accessCardNumber}</span>
              </p>
            </div>
            <Dropdown
              compact
              label={t("PAGES.FOOD_SETTINGS.SELECT_DEAL")}
              placeholder={t("DEFAULT_SELECT.PLACEHOLDER")}
              options={dealDropdownOptions}
              value={selectedDeal}
              onChange={setSelectedDeal}
            />
            <Button
              success
              isLoading={isPushingMunuUser}
              disabled={!selectedDeal || isPushingMunuUser}
              onClick={pushUserToMunu}
              style={{ alignSelf: "end" }}
            >
              {t("PAGES.FOOD_SETTINGS.CONNECT_BUTTON_TEXT")}
            </Button>
          </div>
        </>
      ) : (
        <div className={styles.foodPlaceholder}>
          <Icon name="phone_android" className="dangerIcon" />
          <p>{t("PAGES.FOOD_SETTINGS.USER_HAS_NO_PHONE_NUMBER")}</p>
        </div>
      )}
    </>
  );
};

const MunuUserOverview = ({
  user,
  munuUser,
  accessCardNumber,
  subdivisionId,
}: {
  user: User;
  munuUser: MunuUserData;
  accessCardNumber: string;
  subdivisionId?: string;
}) => {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();

  const handleDeactivateUser = async () => {
    setIsDeactivating(true);
    try {
      await deactivateMunuUser({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        email: user.email,
        munuUserId: munuUser.Id,
        buildingTenantId: user.buildingTenantId,
        ...(subdivisionId && { subdivisionId }),
      });
      addNotification(
        t("NOTIFICATIONS.DOCUMENT_UPDATED", {
          entity: `${user.firstName} ${user.lastName}`,
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [
          getBuildingId(),
          "munu-company-users",
          user.buildingTenantId,
        ],
      });
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: `${user.firstName} ${user.lastName}`,
        },
        fallbackKey: "UPDATE_FAILED",
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleReactivateUser = async () => {
    setIsReactivating(true);
    try {
      await reactivateMunuUser({
        munuUserId: munuUser.Id,
        buildingTenantId: user.buildingTenantId,
        ...(subdivisionId && { subdivisionId }),
      });
      addNotification(
        t("NOTIFICATIONS.DOCUMENT_UPDATED", {
          entity: `${user.firstName} ${user.lastName}`,
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [
          getBuildingId(),
          "munu-company-users",
          user.buildingTenantId,
        ],
      });
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: `${user.firstName} ${user.lastName}`,
        },
        fallbackKey: "UPDATE_FAILED",
      });
    } finally {
      setIsReactivating(false);
    }
  };

  const userDetailsMapping: {
    label: string;
    munuKey: keyof MunuUserData;
    welcomeKey?: keyof User;
    value?: string;
  }[] = [
    {
      label: t("USERS.TABLE_HEADERS.FIRST_NAME"),
      munuKey: "FirstName",
      welcomeKey: "firstName",
    },
    {
      label: t("USERS.TABLE_HEADERS.LAST_NAME"),
      munuKey: "LastName",
      welcomeKey: "lastName",
    },
    {
      label: t("USERS.TABLE_HEADERS.PHONE"),
      munuKey: "MobilePhone",
      welcomeKey: "phone",
    },
    {
      label: t("USERS.TABLE_HEADERS.EMAIL"),
      munuKey: "EmailAddress",
      welcomeKey: "email",
    },
    {
      label: t("PAGES.FOOD_SETTINGS.ACCESS_CARD_NUMBER"),
      munuKey: "CardNumber",
      value: accessCardNumber,
    },
    { label: t("PAGES.FOOD_SETTINGS.ACCOUNT_TYPE"), munuKey: "AccountType" },
    { label: t("PAGES.FOOD_SETTINGS.DEAL_ID"), munuKey: "DealId" },
    { label: t("PAGES.FOOD_SETTINGS.BENEFIT_NAME"), munuKey: "BenefitName" },
    /* { label: t("QR_CODE"), munuKey: "QRCode" }, */
  ];
  const isOutOfSync = userDetailsMapping.some(
    ({ munuKey, welcomeKey, value }) => {
      const munuVal = munuUser[munuKey];
      const welcomeVal = (welcomeKey ? user[welcomeKey] : value) as
        string | undefined;
      return welcomeVal && welcomeVal.replace("+", "") !== munuVal;
    },
  );

  const updateUser = async () => {
    setIsUpdating(true);
    try {
      await updateMunuUser({
        userId: munuUser.Id + "",
        phone: user.phone,
        email: user.email,
        lastName: user.lastName,
        firstName: user.firstName,
        accessCard: accessCardNumber,
        buildingTenantId: user.buildingTenantId,
        ...(subdivisionId && { subdivisionId }),
      });
      // Add success notification if update is successful
      addNotification(
        t("NOTIFICATIONS.DOCUMENT_UPDATED", {
          entity: `${user.firstName} ${user.lastName}`,
        }),
        NotificationType.SUCCESS,
      );
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "munu-company-users"],
      });
    } catch (error) {
      console.log(error);
      // Add error notification if update fails
      addNotification(
        t("NOTIFICATIONS.UPDATE_FAILED", {
          entity: `${user.firstName} ${user.lastName}`,
        }),
      );
    }
    setIsUpdating(false);
  };

  if (munuUser.Status === "Deactivated") {
    return (
      <>
        <div
          className={styles.foodPlaceholder}
          style={{
            gap: "var(--wlcm-spacing-xs)",
          }}
        >
          <Icon
            name="no_burger"
            className="dangerIcon"
            style={{
              marginBottom: "var(--wlcm-spacing-xs)",
            }}
          />
          <h2 className="h3">{t("PAGES.FOOD_SETTINGS.USER_DISABLED")}</h2>
          <p>{t("PAGES.FOOD_SETTINGS.USER_DISABLED_INFO")}</p>
        </div>
        <Button
          icon="burger"
          disabled={isReactivating}
          isLoading={isReactivating}
          onClick={handleReactivateUser}
          style={{ margin: "0 auto var(--wlcm-spacing-md)" }}
        >
          {t("PAGES.FOOD_SETTINGS.REACTIVATE_USER")}
        </Button>
      </>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--wlcm-spacing-sm)",
      }}
    >
      <h2 className="sr-only">{t("PAGES.FOOD_SETTINGS.USER_CONNECTED")}</h2>
      <div className={styles.divWithIcon}>
        <Icon name="check_circle" className="greenIcon" />
        <p>
          {t("PAGES.FOOD_SETTINGS.IS_CONNECTED_WITH")}{" "}
          {t("PAGES.FOOD_SETTINGS.ACCESS_CARD_NUMBER").toLowerCase()}:{" "}
          <span className="monospace">{accessCardNumber}</span>
        </p>
      </div>
      {isOutOfSync && (
        <div
          className="banner warningBanner"
          style={{ justifyContent: "space-between", flexWrap: "wrap" }}
        >
          <div>
            <Icon name="warning" />
            <p>{t("PAGES.FOOD_SETTINGS.UPDATE_TEXT")}</p>
          </div>
          <Button dark icon="sync" isLoading={isUpdating} onClick={updateUser}>
            {t("PAGES.FOOD_SETTINGS.UPDATE_BUTTON_TEXT")}
          </Button>
        </div>
      )}
      <div className="tableContainer">
        <table className={styles.userDataTable}>
          <thead>
            <tr>
              <td></td>
              <th scope="col" style={{ fontSize: "var(--wlcm-text-sm)" }}>
                <Trans
                  i18nKey="PAGES.FOOD_SETTINGS.MUNU_USER_DETAILS"
                  components={{
                    emphasize: (
                      <span
                        style={{
                          display: "block",
                          fontSize: "var(--wlcm-text-default)",
                        }}
                      />
                    ),
                  }}
                />
              </th>
              <th scope="col" style={{ fontSize: "var(--wlcm-text-sm)" }}>
                <Trans
                  i18nKey="PAGES.FOOD_SETTINGS.WELCOME_USER_DETAILS"
                  components={{
                    emphasize: (
                      <span
                        style={{
                          display: "block",
                          fontSize: "var(--wlcm-text-default)",
                        }}
                      />
                    ),
                  }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {userDetailsMapping.map(({ label, munuKey, welcomeKey, value }) => {
              const munuVal = munuUser[munuKey];
              const welcomeVal = (welcomeKey ? user[welcomeKey] : value) as
                string | undefined;
              const isDifferent = !!(
                welcomeVal && welcomeVal.replace("+", "") !== munuVal
              );
              return (
                <tr
                  key={munuKey}
                  className={isDifferent ? styles.highlightRow : ""}
                >
                  <th scope="row">{label}</th>
                  <td>{munuVal}</td>
                  <td>{welcomeVal ?? ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Button
        icon="no_burger"
        isLoading={isDeactivating}
        disabled={isDeactivating}
        danger
        onClick={handleDeactivateUser}
        style={{ margin: "var(--wlcm-spacing-md) auto 0 0" }}
      >
        {t("PAGES.FOOD_SETTINGS.DISABLE_USER")}
      </Button>
    </div>
  );
};
